import { useState, useEffect, useMemo } from "react";
import { useAuth } from "@/context/AuthContext";
import { api, apiErr } from "@/lib/api";
import { useApi } from "@/lib/useApi";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { PageHeader } from "@/components/common";
import { flatNavForUser } from "@/components/AppShell";
import { Sparkles, Save, RotateCcw, GripVertical, Star, Loader2, GraduationCap, Grid3x3, Wand2 } from "lucide-react";
import { toast } from "sonner";

const LOCKED = ["/dashboard"];

export default function Rollskraddare() {
  const { user, effectiveRole, refreshUser } = useAuth();
  const { data, reload } = useApi("/role-tailor");
  const ALL = useMemo(
    () => flatNavForUser(user, effectiveRole).filter((n) => n.to !== "/rollskraddare"),
    [user, effectiveRole]
  );
  const byTo = useMemo(() => Object.fromEntries(ALL.map((n) => [n.to, n])), [ALL]);

  const [hidden, setHidden] = useState([]);
  const [pinned, setPinned] = useState([]);
  const [order, setOrder] = useState([]);
  const [dragTo, setDragTo] = useState(null);
  const [wish, setWish] = useState("");
  const [thinking, setThinking] = useState(false);
  const [ai, setAi] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!data || !ALL.length) return;
    const p = data.prefs || {};
    setHidden((p.hidden || []).filter((to) => byTo[to]));
    setPinned((p.pinned || []).filter((to) => byTo[to]));
    const saved = (p.order || []).filter((to) => byTo[to]);
    setOrder([...saved, ...ALL.map((n) => n.to).filter((to) => !saved.includes(to))]);
    setAi(data.ai || null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, ALL]);

  const toggleHide = (to) => setHidden((h) => (h.includes(to) ? h.filter((x) => x !== to) : [...h, to]));
  const togglePin = (to) =>
    setPinned((p) => (p.includes(to) ? p.filter((x) => x !== to) : p.length >= 6 ? p : [...p, to]));
  const onDrop = (to) => {
    if (!dragTo || dragTo === to) return setDragTo(null);
    setOrder((o) => { const a = [...o]; a.splice(a.indexOf(dragTo), 1); a.splice(a.indexOf(to), 0, dragTo); return a; });
    setDragTo(null);
  };

  const suggest = async () => {
    setThinking(true);
    try {
      const items = ALL.map((n) => ({ to: n.to, label: n.label }));
      const r = await api.post("/role-tailor/suggest", { wish, items });
      setAi(r.data);
      toast.success("AI-förslag klart – tryck \"Tillämpa\" för att använda");
    } catch (e) { toast.error(apiErr(e)); } finally { setThinking(false); }
  };

  const applyAi = () => {
    if (!ai) return;
    setHidden((ai.hidden || []).filter((to) => byTo[to]));
    setPinned((ai.pinned || []).filter((to) => byTo[to]).slice(0, 6));
    const o = (ai.order || []).filter((to) => byTo[to]);
    setOrder([...o, ...ALL.map((n) => n.to).filter((to) => !o.includes(to))]);
    toast.success("Förslaget tillämpat – glöm inte att spara");
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/role-tailor", { hidden, order, pinned, ai });
      await refreshUser();
      toast.success("Din personliga meny är sparad");
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  const reset = async () => {
    if (!window.confirm("Återställ till standardmenyn?")) return;
    try {
      await api.post("/role-tailor/reset");
      setHidden([]); setPinned([]); setAi(null);
      setOrder(ALL.map((n) => n.to));
      await refreshUser();
      reload();
      toast.success("Återställt till standard");
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="mx-auto max-w-3xl" data-testid="rollskraddare-page">
      <PageHeader title="Rollskräddare" icon={Sparkles}
        subtitle={`Skräddarsy din egen vy som ${data?.role_label || "din roll"}. Detta påverkar bara dig – inte organisationens inställningar.`} />

      <Card className="mb-5 space-y-3 p-4" data-testid="rt-ai-card">
        <div className="flex items-center gap-2 text-sm font-semibold"><Wand2 className="h-4 w-4 text-accent" />Be AI om förslag</div>
        <Textarea rows={3} value={wish} onChange={(e) => setWish(e.target.value)} data-testid="rt-wish"
          placeholder="Beskriv vad du vill ha ut av verktyget i din roll, t.ex. 'Jag jobbar mest med schema och kompetens, vill snabbt se mitt teams belastning.'" />
        <div className="flex flex-wrap gap-2">
          <Button onClick={suggest} disabled={thinking} data-testid="rt-suggest">
            {thinking ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Sparkles className="mr-1 h-4 w-4" />}
            {thinking ? "Tänker…" : "Ge förslag"}
          </Button>
          {ai && <Button variant="outline" onClick={applyAi} data-testid="rt-apply">Tillämpa förslag</Button>}
        </div>
        {ai && (
          <div className="space-y-3 rounded-lg border bg-accent/5 p-3" data-testid="rt-ai-result">
            {ai.rationale && <p className="text-sm italic text-muted-foreground">{ai.rationale}</p>}
            {(ai.trainings || []).length > 0 && (
              <div>
                <div className="mb-1 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground"><GraduationCap className="h-3.5 w-3.5" />Utbildningsförslag</div>
                <div className="flex flex-wrap gap-1.5">{ai.trainings.map((x, i) => <Badge key={i} variant="secondary" data-testid={`rt-train-${i}`}>{x}</Badge>)}</div>
              </div>
            )}
            {(ai.competences || []).length > 0 && (
              <div>
                <div className="mb-1 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground"><Grid3x3 className="h-3.5 w-3.5" />Kompetensförslag</div>
                <div className="flex flex-wrap gap-1.5">{ai.competences.map((x, i) => <Badge key={i} variant="outline" data-testid={`rt-comp-${i}`}>{x}</Badge>)}</div>
              </div>
            )}
          </div>
        )}
      </Card>

      <Card className="p-0" data-testid="rt-menu">
        <div className="border-b p-4 text-sm text-muted-foreground">
          Dra i handtaget för att ändra ordning, bocka i/ur vad som syns och stjärnmärk upp till 6 genvägar (visas på din Dashboard och längst ner i mobilen). Dashboard är alltid synlig.
        </div>
        <div className="divide-y">
          {order.map((to) => {
            const n = byTo[to];
            if (!n) return null;
            const Icon = n.icon;
            const locked = LOCKED.includes(to);
            const isPinned = pinned.includes(to);
            return (
              <div key={to} draggable onDragStart={() => setDragTo(to)} onDragEnd={() => setDragTo(null)}
                onDragOver={(e) => e.preventDefault()} onDrop={() => onDrop(to)}
                className={`flex items-center gap-3 p-3 ${dragTo === to ? "bg-secondary" : ""}`} data-testid={`rt-item-${to.slice(1)}`}>
                <GripVertical className="h-4 w-4 cursor-grab text-muted-foreground" data-testid={`rt-drag-${to.slice(1)}`} />
                <Checkbox checked={locked || !hidden.includes(to)} disabled={locked} onCheckedChange={() => toggleHide(to)} data-testid={`rt-show-${to.slice(1)}`} />
                {Icon && <Icon className="h-4 w-4 text-muted-foreground" />}
                <span className={`flex-1 text-sm ${locked ? "text-muted-foreground" : ""}`}>{n.label}{locked ? " (alltid på)" : ""}</span>
                <button onClick={() => togglePin(to)} data-testid={`rt-pin-${to.slice(1)}`}
                  className={`rounded-md p-1.5 transition-colors ${isPinned ? "text-amber-500" : "text-muted-foreground hover:text-amber-500"}`}>
                  <Star className="h-4 w-4" fill={isPinned ? "currentColor" : "none"} />
                </button>
              </div>
            );
          })}
        </div>
        <div className="flex items-center justify-between border-t p-4">
          <Button variant="ghost" onClick={reset} data-testid="rt-reset"><RotateCcw className="mr-1 h-4 w-4" />Återställ</Button>
          <Button onClick={save} disabled={saving} data-testid="rt-save"><Save className="mr-1 h-4 w-4" />{saving ? "Sparar…" : "Spara min meny"}</Button>
        </div>
      </Card>
    </div>
  );
}
